import { isValidElement } from 'react';
import { countBy, findLast, isPlainObject, range, uniq, uniqBy } from 'lodash';
import { INVALID_EXPRESSION } from 'common/constants/messages/validations';
import { Cells, InvalidCell } from 'common/types/scalarSpreadsheet';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { EQUITY_ALLOCATION_KEY } from 'pages/ValuationsAllocation/common/constants/equityAllocation';
import { VALUATION_SUMMARY_KEY } from 'pages/ValuationsAllocation/common/constants/valuationSummary';
import {
  InvalidCellEntry,
  InvalidTable,
  SlimApproachTable,
  SlimApproachTables,
} from 'pages/ValuationsAllocation/types';
import { getInvalidSheetTableName } from 'pages/ValuationsAllocation/util/getInvalidSheetTableName';
import { getColumnIndex, getColumnLetter } from 'utilities/alphabet-utilities';
import { GetInvalidTablesParams } from './types';

const INVALID_VALUE_MESSAGE = 'This value is invalid.';
const SHEET_REFERENCE = /([A-Za-z_]\w*)\.\w+/g;

type LabelContext = Pick<GetInvalidTablesParams, 'allocationMethods' | 'tableNames'>;

interface RowConfigItem {
  alias?: string;
  parent?: string | null;
  value?: unknown;
}

// Plain text a title shows: a string, or the name a title component renders.
const asLabel = (value: unknown): string => {
  const text = isValidElement<{ name?: unknown; value?: unknown }>(value)
    ? (value.props.name ?? value.props.value)
    : value;
  return typeof text === 'string' ? text.trim() : '';
};

// Header text as displayed; select headers store an id and carry the label for it.
const headerText = (cell?: ICell) => asLabel(cell?.valueLabels?.[String(cell.value)] ?? cell?.value);

// True for the main Equity Allocation sheet and its related-entity copies.
export const isEquityAllocationSheet = (sheetName: string) =>
  sheetName === EQUITY_ALLOCATION_KEY || sheetName.startsWith(`${EQUITY_ALLOCATION_KEY}_`);

// Aliases of every row group above a row, closest first.
const getRowGroups = (parent: RowConfigItem['parent'], rowConfig: RowConfigItem[], found: string[] = []): string[] =>
  !parent || found.includes(parent)
    ? found
    : getRowGroups(rowConfig.find(row => row?.alias === parent)?.parent, rowConfig, [...found, parent]);

// Scenario columns describe their allocation scenario in the row-2 entry (Valuation Summary, Equity Allocation).
const isScenarioColumn = (column: unknown, headerAlias = '') => {
  const entry = (column as Record<string, unknown> | undefined)?.[headerAlias];
  return isPlainObject(entry) && 'scenarioRef' in (entry as object);
};

// The row-1 header over a column: its own cell, or a merged cell spanning from the left.
const getGroupHeader = (cells: Cells, columnIndex: number) => {
  const start = findLast(range(columnIndex + 1), index => Boolean(cells[`${getColumnLetter(index)}1`]));
  if (start === undefined) {
    return undefined;
  }
  const header = cells[`${getColumnLetter(start)}1`];
  return start + (header.colSpan ?? 1) > columnIndex ? header : undefined;
};

// The header over a cell as the user reads it: a scenario column shows its allocation method (else
// "Scenario N"); any other column shows the header rows above it, merged groups included.
const getColumnLabel = (
  cell: InvalidCell,
  rowConfig: RowConfigItem[],
  rowNumber: number,
  { allocationMethods }: LabelContext
) => {
  const { cells = {}, columns = [], name: sheetName = '' } = cell.sheet ?? {};
  const columnIndex = cell.y ?? getColumnIndex(cell.columnLegend) ?? 0;
  const columnLetter = getColumnLetter(columnIndex);
  const headerAlias = rowConfig[1]?.alias;

  if (isScenarioColumn(columns[columnIndex], headerAlias)) {
    const methodName = allocationMethods.find(({ id }) => id === cells[`${columnLetter}2`]?.value)?.name;
    const scenarioNumber = columns
      .slice(0, columnIndex + 1)
      .filter(column => isScenarioColumn(column, headerAlias)).length;
    const entityTitle =
      isEquityAllocationSheet(sheetName) && sheetName !== EQUITY_ALLOCATION_KEY ? asLabel(rowConfig[0]?.value) : '';
    return [entityTitle, methodName || `Scenario ${scenarioNumber}`].filter(Boolean).join(' - ');
  }

  // A merged row-1 cell means row 2 holds the per-column headers under it.
  const hasMergedHeader = Object.entries(cells).some(
    ([key, header]) => /^[A-Z]+1$/.test(key) && (header?.colSpan ?? 1) > 1
  );
  const subheader = cells[`${columnLetter}2`];
  return [
    rowNumber > 1 ? headerText(getGroupHeader(cells, columnIndex)) : '',
    hasMergedHeader && rowNumber > 2 && subheader?.gridType === 'string' ? headerText(subheader) : '',
  ]
    .filter(Boolean)
    .join(' ');
};

// The next step for a formula that could not be evaluated: fix it when the cell is editable, otherwise
// refresh the approach it reads from.
const describeBrokenExpression = (cell: InvalidCell, { tableNames }: LabelContext) => {
  if (!cell.readOnly) {
    return `This value could not be calculated; check the formula in cell ${cell.key ?? ''}.`;
  }
  const sources = uniq(
    [...String(cell.expr ?? '').matchAll(SHEET_REFERENCE)]
      .map(([, sheetName]) => tableNames[getInvalidSheetTableName(sheetName, Object.keys(tableNames))]?.name)
      .filter(Boolean)
  );
  const source = sources.join(' and ') || 'another approach';
  return `This value could not be calculated from ${source}; recalculate that approach or reload the page.`;
};

export const describeInvalidCell = (cell: InvalidCell, context: LabelContext): InvalidCellEntry => {
  const rowConfig: RowConfigItem[] = cell.sheet?.rowConfig ?? [];
  const rowNumber = cell.rowNumber ?? (cell.x ?? 0) + 1;
  const row = rowConfig[rowNumber - 1];
  const rowGroups = getRowGroups(row?.parent, rowConfig);
  const [parentGroup] = rowGroups;
  const rowTitle = asLabel(row?.value) || `Row ${rowNumber}`;
  // The parent group is only named when the same title appears elsewhere in the sheet.
  const isRepeatedTitle = rowConfig.filter(configRow => asLabel(configRow?.value) === rowTitle).length > 1;
  const parentTitle =
    parentGroup && isRepeatedTitle ? asLabel(rowConfig.find(configRow => configRow?.alias === parentGroup)?.value) : '';
  const tooltipMessages = cell.tooltipMessages ?? [];
  const isExpressionMessage = (message: string) => message.startsWith(INVALID_EXPRESSION);
  const hasBrokenExpression = Boolean(cell.invalidExpr) || tooltipMessages.some(isExpressionMessage);
  const message = uniq([
    ...tooltipMessages.filter(tooltip => !isExpressionMessage(tooltip)),
    ...(hasBrokenExpression ? [describeBrokenExpression(cell, context)] : []),
  ]).join(' ');

  return {
    cellKey: cell.key ?? '',
    columnLabel: getColumnLabel(cell, rowConfig, rowNumber, context),
    hidden: Boolean(cell.hidden) || cell.isVisible === false,
    message: message || INVALID_VALUE_MESSAGE,
    parentColumn: cell.parentColumn ? String(cell.parentColumn) : undefined,
    readOnly: Boolean(cell.readOnly),
    rowGroups,
    rowLabel: [parentTitle, rowTitle].filter(Boolean).join(' / '),
    sheetName: cell.sheet?.name ?? '',
  };
};

// Adds the column letter, or the cell key within one column, where two cells of a table would read the same.
export const withDistinctLabels = (entries: InvalidCellEntry[]) => {
  const labelOf = ({ columnLabel, rowLabel }: InvalidCellEntry) => `${rowLabel}|${columnLabel}`;
  const columnOf = ({ cellKey }: InvalidCellEntry) => cellKey.replace(/\d+$/, '');
  const labelCounts = countBy(entries, labelOf);
  const columnCounts = countBy(entries, entry => `${labelOf(entry)}|${columnOf(entry)}`);

  return entries.map(entry => {
    if (labelCounts[labelOf(entry)] < 2) {
      return entry;
    }
    const coordinate =
      columnCounts[`${labelOf(entry)}|${columnOf(entry)}`] > 1 ? `cell ${entry.cellKey}` : `column ${columnOf(entry)}`;
    return { ...entry, columnLabel: [entry.columnLabel, coordinate].filter(Boolean).join(', ') };
  });
};

// One line of banner text for an invalid cell.
export const formatInvalidCell = ({ columnLabel, hidden, message, readOnly, rowLabel }: InvalidCellEntry) => {
  const column = columnLabel ? ` (${columnLabel})` : '';
  const flag = (hidden && ' (hidden)') || (readOnly && ' (read-only)') || '';
  return `${rowLabel}${column} - ${message}${flag}`;
};

// The tab a banner group points to; approaches that share a name (e.g. two "Transaction Comps") are numbered.
const getTableHeading = (tableKey: string, tableNames: SlimApproachTables): SlimApproachTable => {
  const table = tableNames[tableKey];
  if (!table) {
    return { approachType: '', id: VALUATION_SUMMARY_KEY, name: tableKey };
  }
  const sameNameKeys = Object.keys(tableNames).filter(key => tableNames[key].name === table.name);
  return sameNameKeys.length > 1 ? { ...table, name: `${table.name} #${sameNameKeys.indexOf(tableKey) + 1}` } : table;
};

// Groups invalid cells by the tab that shows them, with a readable description per cell.
const getInvalidTables = ({ allocationMethods, invalidCells, tableNames }: GetInvalidTablesParams): InvalidTable[] => {
  const context = { allocationMethods, tableNames };
  const tableKeys = Object.keys(tableNames);
  const cellsByTable = new Map<string, InvalidCellEntry[]>();

  uniqBy(invalidCells, cell => `${cell.sheet?.name}|${cell.key}`).forEach(cell => {
    const tableKey = getInvalidSheetTableName(cell.sheet?.name ?? '', tableKeys);
    cellsByTable.set(tableKey, [...(cellsByTable.get(tableKey) ?? []), describeInvalidCell(cell, context)]);
  });

  return [...cellsByTable].map(([tableKey, cells]) => ({
    ...getTableHeading(tableKey, tableNames),
    cells: withDistinctLabels(cells),
  }));
};

export default getInvalidTables;
